import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { TipoLancamento } from '@models/tipo-lancamento';
import { LancamentoDoLote } from '../../models/lancamento-do-lote';

@Component({
  selector: 'app-card-lote-lancamentos',
  standalone: false,
  templateUrl: './card-lote-lancamentos.component.html',
  styleUrl: './card-lote-lancamentos.component.scss'
})
export class CardLoteLancamentosComponent {

  @Input() tiposLancamento: TipoLancamento[] = [];
  @Input() titulo: string = "";
  @Input() form: FormGroup;
  @Input() prefix!: string;
  @Input() tabCruzado: boolean = true;

  @Output() tabFromLastRow = new EventEmitter<void>();

  @ViewChildren('valorInput', { read: ElementRef }) 
  valorInputs!: QueryList<ElementRef>;

  get rows(): FormArray<FormGroup> {
    return this.form.get('rows') as FormArray<FormGroup>;
  }

  get total(): number {
    const rows = this.form.get('rows') as FormArray | null;
    if (!rows) return 0;
    return rows.controls.reduce((acc, row) => acc + (Number(row.get('valor')?.value) || 0), 0);
  }

  selectedRows: FormGroup[] = [];

  get totalSelecionado(): number {
    return this.selectedRows.reduce((acc, row) => acc + (Number(row.get('valor')?.value) || 0), 0);
  }

  isUpdatingValidity: boolean = false;

  constructor(
    private host: ElementRef<HTMLElement>,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.form.addControl('rows', this.fb.array([]));
    this.addRowAndFocus();
  }

  private createRow(lancamento: LancamentoDoLote | null, tipoLancamentoDefault: TipoLancamento | null = null): void {
    let tipoLancamento: TipoLancamento | null | undefined = tipoLancamentoDefault;
    if (lancamento)
      tipoLancamento = this.tiposLancamento.find(x => x.id === lancamento?.tipoLancamentoId);

    const formGroup = this.fb.group({
      id: [lancamento?.id],
      valor: [lancamento?.valor, this.campoNecessarioSeAlgumPreenchidoValidator],
      tipoLancamento: [tipoLancamento, this.campoNecessarioSeAlgumPreenchidoValidator],
      observacoes: [lancamento?.observacao]
    });

    type ControlKey = keyof typeof formGroup.controls;
    const keys = Object.keys(formGroup.controls) as ControlKey[];

    for (const key of keys) {
      const formControl = formGroup.controls[key] as FormControl;
      if (key !== 'valor')
        formControl.valueChanges.subscribe(() => this.updateValidityFormControl(formGroup.controls.valor));
      if (key !== 'tipoLancamento')
        formControl.valueChanges.subscribe(() => this.updateValidityFormControl(formGroup.controls.tipoLancamento));
    }

    this.rows.push(formGroup);
  }

  private campoNecessarioSeAlgumPreenchidoValidator(control: FormControl): { required_if_not_empty: boolean } | null {
    if (control.value != null && control.value != '')
      return null;

    const parent = control.parent as FormGroup | null;
    if (!parent) {
      return { required_if_not_empty: true };
    }

    const naoPreenchido = Object.keys(parent.controls).every(key => {
      const otherControl = parent.controls[key];
      return otherControl.value == null || otherControl.value == '';
    });
    return naoPreenchido ? null : { required_if_not_empty: true };
  }

  private updateValidityFormControl(control: FormControl): void {
    if (this.isUpdatingValidity) return;

    this.isUpdatingValidity = true;
    control.updateValueAndValidity();
    this.isUpdatingValidity = false;
  }

  public addRowAndFocus(): void {
    const lastTipo = this.rows.length > 0
      ? (this.rows.at(this.rows.length - 1).get('tipoLancamento')?.value as TipoLancamento | null)
      : null;
    this.createRow(null, lastTipo);

    this.focusValorInput(this.rows.length - 1);
  }

  public focusOrAddRowAndFocus(): void {
    const lastRow = this.rows.length > 0 ? this.rows.at(this.rows.length - 1) : null;
    const isLastRowEmpty = lastRow
      ? Object.values(lastRow.controls).every(c => c.value == null || c.value === '')
      : false;

    if (isLastRowEmpty) {
      this.focusValorInput(this.rows.length - 1);
    } else {
      this.addRowAndFocus();
    }
  }

  private focusValorInput(rowIndex: number): void {
    // Wait a tick for the DOM to update, then focus
    setTimeout(() => {
      const inputs = this.valorInputs.toArray();
      const target = inputs[rowIndex];
      // PrimeNG p-inputNumber renders an input inside its host, so query it
      const nativeInput = target?.nativeElement.querySelector('input:not([type=hidden])');
      if (nativeInput) {
        nativeInput.focus();
      }
    }, 0);
  }

  public createNewRowOnTab(event: KeyboardEvent, rowIndex: number, field: string): void {
    const isLastField = field === 'observacoes';
    const isLastRow = rowIndex === this.rows.length - 1;

    if (event.key === 'Tab' && !event.shiftKey && isLastField && isLastRow) {
      event.preventDefault();
      if (this.tabCruzado) {
        setTimeout(() => this.tabFromLastRow.emit(), 0);
      } else {
        setTimeout(() => this.addRowAndFocus(), 0);
      }
    }
  }

  public tabToNextComponentOnAutocomplete(event: KeyboardEvent, rowIndex: number, field: string) {
    if (event.key !== 'Tab')
        return;

    event.preventDefault();
    
    const fieldOrder = ['valor', 'tipoLancamento', 'observacoes'];
    const nextField = fieldOrder[fieldOrder.indexOf(field) + 1];

    // Delay required to avoid blur before focus
    setTimeout(() => {
      // Find the next input in the same row
      const rows = this.host.nativeElement.querySelectorAll('.row-group');
      const currentRow = rows[rowIndex] as HTMLElement;

      if (nextField) {
        // Próximo campo na mesma linha
        const nextElement = currentRow.querySelector(
          `[data-field="${nextField}"] input,
           [data-field="${nextField}"] textarea,
           [data-field="${nextField}"]`
        ) as HTMLElement;
  
        nextElement?.focus();
      } else {
        // Último campo → próxima linha
        const nextRow = rows[rowIndex + 1] as HTMLElement;
  
        if (!nextRow) return;
  
        const firstElement = nextRow.querySelector(
          `[data-field="${fieldOrder[0]}"] input,
           [data-field="${fieldOrder[0]}"]`
        ) as HTMLElement;
        
        firstElement?.focus();
      }
    });

  }

  public selectInputText(event: any): void {
    // The event contains the input element
    event.target?.select();
  }

  public onAutoCompleteBlur(event: any) {
    event.target.blur();
    event.originalEvent?.preventDefault();
  }

  public getFormGroup(index: number): FormGroup {
    return this.rows.at(index);
  }

  public reset(): void {
    this.rows.clear();
    this.selectedRows = [];
    this.addRowAndFocus();
  }

  public addLancamentosToForm(lancamentos: LancamentoDoLote[]): void {
    this.removeEmptyRows();
    lancamentos.forEach(l => {
      this.createRow(l);
    });
  }

  public definirValorPrimeiraLinha(valor: number): void {
    const primeiraLinha = this.rows.length > 0 ? this.rows.at(0) : null;
    primeiraLinha?.get('valor')?.setValue(valor);
  }

  private removeEmptyRows(): void {
    this.selectedRows = [];
    for (let i = this.rows.length - 1; i >= 0; i--) {
      const group = this.rows.at(i) as FormGroup;
  
      const temAlgumValor = Object.values(group.controls).some(control => {
        const valor = control.value;
  
        return valor !== null &&
               valor !== undefined &&
               valor !== '';
      });
  
      if (!temAlgumValor) {
        this.rows.removeAt(i);
      }
    }
  }
}
